import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Clock, PiggyBank } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import {
  useAccountCredits,
  useGroupCredits,
  usePaymentApplications,
  type CreditApplication,
  type CreditRow,
} from '@/hooks/useCredits';
import {
  useCreditRequests,
  useWithdrawCreditRequest,
  type CreditRequest,
} from '@/hooks/useCreditRequests';
import CreditRequestForm from './CreditRequestForm';

/**
 * Unapplied payments & credits — money we have taken that is not yet sitting on
 * an invoice, and what each payment DID land on.
 *
 * The portal used to have nowhere to say this. A cheque keyed into QuickBooks
 * has no orders.payments row, and orders.v_invoices_all filters
 * `txn_type IN ('Invoice','SalesReceipt')`, so an unapplied payment could not
 * appear on any surface — which is how THE MELT MAIN came to be shown owing
 * $9,918.00 against a QuickBooks balance of $2,792.70, the $7,125.30 gap being
 * 15 payments dating back to 2024-11-08.
 *
 * Renders the chain roster for a chain accounting login and the single account
 * for everyone else — same panel, so the two can't tell different stories.
 *
 * ⚠ A CUSTOMER CANNOT APPLY A CREDIT HERE, on purpose. Which invoice a payment
 * belongs to is a bookkeeping decision made in QuickBooks. What they CAN do is
 * tell us — "these invoices", "my oldest ones", or "mail me a check" — which
 * files a credit request and emails the team (customer-credit-requests). The
 * request closes itself once QuickBooks shows the credit used.
 */

type PanelRow = CreditRow & { appliedTo?: CreditApplication[]; storeLabel?: string | null };

export default function CreditsPanel() {
  const group = useGroupCredits();
  const account = useAccountCredits();
  const isGroup = group.data?.is_group === true;

  const rows: PanelRow[] = useMemo(() => {
    if (isGroup) {
      return (group.data?.credits ?? []).map((c) => ({
        kind: c.kind,
        sourceId: c.source_id,
        txnDate: c.txn_date,
        reference: c.reference,
        method: c.method,
        receivedAmount: c.received_amount,
        appliedAmount: c.applied_amount,
        creditAmount: c.credit_amount,
        appliedToCount: c.applied_to.length,
        ageDays: c.age_days,
        appliedTo: c.applied_to.map((a) => ({
          qboInvoiceId: a.qbo_invoice_id,
          invoiceNumber: a.invoice_number,
          invoiceDate: a.invoice_date,
          amount: a.amount,
        })),
        storeLabel: c.store_name,
      }));
    }
    return account.data ?? [];
  }, [isGroup, group.data, account.data]);

  // Applications for the single-account view are a separate read; the chain
  // endpoint already carries them.
  const paymentIds = useMemo(
    () => (isGroup ? [] : rows.filter((r) => r.kind === 'payment').map((r) => r.sourceId)),
    [isGroup, rows],
  );
  const apps = usePaymentApplications(paymentIds);

  const requests = useCreditRequests();
  const withdraw = useWithdrawCreditRequest();
  const openRequests = useMemo(() => {
    const m = new Map<string, CreditRequest>();
    for (const r of requests.data?.requests ?? []) {
      if (r.status === 'open') m.set(`${r.source_kind}:${r.source_id}`, r);
    }
    return m;
  }, [requests.data]);
  const [asking, setAsking] = useState<string | null>(null);
  const [sentNote, setSentNote] = useState<string | null>(null);

  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isLoading = isGroup ? group.isLoading : account.isLoading || group.isLoading;
  // ⚠ A failed read must never render as "no credits" — that reads as money
  // gone. The group query failing is not fatal on its own (it answers
  // is_group:false for most logins); the account query failing is.
  const error = account.error instanceof Error ? account.error.message : null;

  const total = rows.reduce((s, r) => s + r.creditAmount, 0);
  const oldest = rows.reduce((m, r) => Math.max(m, r.ageDays), 0);

  if (isLoading) {
    return (
      <section className="rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 p-5 shadow-card-deep">
        <div className="h-5 w-56 animate-pulse rounded bg-brix-surface-alt" />
        <div className="mt-3 h-4 w-full animate-pulse rounded bg-brix-surface-alt" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-2xl border-2 border-red-300 bg-red-50/70 p-5">
        <h2 className="font-display text-base font-bold text-red-800">
          We couldn't load your credits
        </h2>
        <p className="mt-1 text-sm text-red-700">{error}</p>
      </section>
    );
  }

  return (
    <section
      className="overflow-hidden rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 shadow-card-deep"
      aria-labelledby="credits-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-brix-border bg-brix-surface-alt/60 px-5 py-4">
        <div>
          <h2
            id="credits-heading"
            className="flex items-center gap-2 font-display text-base font-bold tracking-tight text-brix-ink"
          >
            <PiggyBank size={18} className="text-brix-primary" />
            {rows.length === 0
              ? 'Unapplied payments & credits'
              : `You have ${formatUsd(total)} we've received but haven't applied to an invoice yet`}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-brix-muted">
            {rows.length === 0
              ? 'Money we’ve received that isn’t on an invoice yet would show here.'
              : 'It still counts toward your account — your balance below is shown after it. Tell us what you’d like done with each one.'}
          </p>
        </div>
        {rows.length > 0 && (
          <div className="text-right">
            <div className="font-mono text-2xl font-bold leading-none tracking-tight text-emerald-700 tabular-nums">
              {formatUsd(total)}
            </div>
            <div className="mt-1 text-xs font-semibold text-brix-muted">
              on account · {rows.length} {rows.length === 1 ? 'item' : 'items'}
              {oldest > 120 ? ` · oldest ${Math.floor(oldest / 30)} months` : ''}
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="border-b border-brix-border bg-amber-50/70 px-5 py-4 text-sm leading-relaxed text-brix-ink">
          <p className="font-semibold">How this works</p>
          <p className="mt-1 text-brix-muted">
            You can&apos;t apply these yourself — someone on our accounting team does it in
            QuickBooks. Press <strong className="text-brix-ink">Tell us what to do</strong> on a
            payment and choose: put it on specific invoices, put it on your oldest invoices, or mail
            you a check for it. We usually take care of it within 1–3 business days and may reach
            out to confirm. Once it&apos;s done it disappears from this list.
          </p>
          {sentNote && <p className="mt-2 font-semibold text-emerald-700">{sentNote}</p>}
          {requests.isError && (
            <p className="mt-2 text-red-700">
              We couldn&apos;t check for requests you&apos;ve already sent:{' '}
              {requests.error instanceof Error ? requests.error.message : 'unknown error'}. Refresh
              the page before sending a new one.
            </p>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-brix-muted">
          Every payment we&apos;ve received is fully applied to an invoice — nothing is sitting as
          a credit.
        </p>
      ) : (
        <ul className="divide-y divide-brix-border">
          {rows.map((r) => {
            const id = `${r.kind}:${r.sourceId}`;
            const applied = r.appliedTo ?? apps.data?.get(r.sourceId) ?? [];
            const expandable = r.kind === 'payment';
            const isOpen = open.has(id);
            const req = openRequests.get(id);
            return (
              <li key={id}>
                <div
                  className={cn(
                    'grid grid-cols-[1fr_auto] items-start gap-3 px-5 pb-2 pt-4 sm:grid-cols-[auto_1fr_120px_120px_130px]',
                    expandable && 'cursor-pointer hover:bg-brix-primary-tint/20',
                  )}
                  onClick={expandable ? () => toggle(id) : undefined}
                  role={expandable ? 'button' : undefined}
                  tabIndex={expandable ? 0 : undefined}
                  aria-expanded={expandable ? isOpen : undefined}
                  onKeyDown={
                    expandable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(id);
                          }
                        }
                      : undefined
                  }
                >
                  <span className="hidden pt-1 text-brix-muted sm:block">
                    {expandable ? (
                      isOpen ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )
                    ) : (
                      <span className="inline-block w-4" />
                    )}
                  </span>

                  <div className="min-w-0">
                    <div className="font-semibold text-brix-ink">
                      {r.kind === 'credit_memo' ? 'Credit memo' : 'Payment'}
                      {r.reference ? ` · ${r.reference}` : ''}
                    </div>
                    <div className="mt-0.5 text-sm text-brix-muted">
                      {r.txnDate ? fmtMirrorDate(r.txnDate) : '—'}
                      {r.method ? ` · ${r.method}` : ''}
                      {r.storeLabel ? ` · ${r.storeLabel}` : ''}
                    </div>
                    {expandable && (
                      <div className="mt-0.5 text-xs text-brix-muted sm:hidden">
                        {applied.length > 0
                          ? `Applied to ${applied.length} invoice${applied.length === 1 ? '' : 's'}`
                          : 'Not applied to any invoice yet'}
                      </div>
                    )}
                  </div>

                  <div className="hidden text-right sm:block">
                    <div className="text-xs text-brix-muted">Received</div>
                    <div className="font-mono text-sm tabular-nums text-brix-ink">
                      {formatUsd(r.receivedAmount)}
                    </div>
                  </div>

                  <div className="hidden text-right sm:block">
                    <div className="text-xs text-brix-muted">Applied</div>
                    <div className="font-mono text-sm tabular-nums text-brix-muted">
                      {formatUsd(r.appliedAmount)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs font-semibold text-brix-muted">Left over</div>
                    <div className="font-mono text-lg font-bold tabular-nums text-emerald-700">
                      {formatUsd(r.creditAmount)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:pl-12">
                  {req ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                        <Clock size={13} />
                        {req.request_type === 'refund_check'
                          ? `Check for ${formatUsd(Number(req.requested_amount))} requested`
                          : req.apply_oldest
                            ? 'Asked to apply to your oldest invoices'
                            : `Asked to apply ${formatUsd(Number(req.requested_amount))} to ${(req.invoices ?? []).length} invoice${(req.invoices ?? []).length === 1 ? '' : 's'}`}
                        {` · sent ${fmtMirrorDate(req.created_at.slice(0, 10))} — waiting on our team`}
                      </span>
                      <button
                        type="button"
                        className="text-xs font-semibold text-brix-muted underline hover:text-brix-ink disabled:opacity-50"
                        disabled={withdraw.isPending}
                        onClick={() => {
                          if (!window.confirm('Withdraw this request? Our team will stop working on it.')) return;
                          withdraw.mutate(req.id);
                        }}
                      >
                        Withdraw
                      </button>
                    </>
                  ) : asking === id ? null : (
                    <button
                      type="button"
                      className="rounded-lg bg-brix-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brix-primary/90 disabled:opacity-50"
                      disabled={requests.isLoading || requests.isError}
                      onClick={() => {
                        setSentNote(null);
                        setAsking(id);
                      }}
                    >
                      Tell us what to do
                    </button>
                  )}
                  {withdraw.isError && req && (
                    <span className="text-xs text-red-700">
                      {withdraw.error instanceof Error ? withdraw.error.message : 'Could not withdraw'}
                    </span>
                  )}
                </div>

                {asking === id && !req && (
                  <CreditRequestForm
                    credit={r}
                    storeLabel={r.storeLabel ?? null}
                    openInvoices={(requests.data?.open_invoices ?? []).filter(
                      (i) => !isGroup || !r.storeLabel || i.storeName === r.storeLabel,
                    )}
                    onCancel={() => setAsking(null)}
                    onSent={(msg) => {
                      setAsking(null);
                      setSentNote(msg);
                    }}
                  />
                )}

                {expandable && isOpen && (
                  <div className="border-t border-brix-border bg-brix-surface-alt/40 px-5 py-4 sm:pl-12">
                    {applied.length === 0 ? (
                      <p className="text-sm text-brix-muted">
                        {apps.isLoading
                          ? 'Loading…'
                          : 'This payment hasn’t been applied to any invoice yet — the whole amount is sitting as a credit.'}
                      </p>
                    ) : (
                      <>
                        <p className="mb-2 text-xs font-semibold tracking-tight text-brix-muted">
                          Applied to {applied.length} invoice{applied.length === 1 ? '' : 's'}
                        </p>
                        <ul className="space-y-1">
                          {applied.map((a) => (
                            <li
                              key={a.qboInvoiceId}
                              className="flex items-center justify-between gap-3 text-sm"
                            >
                              <Link
                                to={`/invoices/${encodeURIComponent(a.qboInvoiceId)}`}
                                className="font-semibold text-brix-primary hover:underline"
                              >
                                {a.invoiceNumber ?? `#${a.qboInvoiceId}`}
                              </Link>
                              <span className="flex items-center gap-3">
                                <span className="text-brix-muted">
                                  {a.invoiceDate ? fmtMirrorDate(a.invoiceDate) : ''}
                                </span>
                                <span className="font-mono tabular-nums text-brix-ink">
                                  {formatUsd(a.amount)}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
